import { useCallback, useEffect, useRef, useState } from "react";
import IconButton, { type IconButtonProps } from "ui/buttons/IconButton";
import IconCheck from "ui/icons/IconCheck";
import IconCopy from "ui/icons/IconCopy";

// How long the success-confirmation (check glyph + "copied" tooltip) stays
// visible after a successful copy. Long enough that the user has a moment
// to register the swap (and re-hover to see the tooltip), short enough that
// it doesn't feel stuck.
const COPIED_RESET_MS = 3000;

export interface CopyButtonProps extends Omit<IconButtonProps, "children" | "ariaLabel" | "tooltip" | "onClick"> {
  /** Text to write to the clipboard when the button is clicked. */
  readonly text: string;
  /** Tooltip shown at rest. Defaults to "Copy". */
  readonly copyLabel?: string;
  /** Tooltip + aria-label shown immediately after a successful copy. */
  readonly copiedLabel?: string;
}

/**
 * One-click clipboard copy with a tooltip on hover and a check-glyph
 * confirmation immediately after a successful copy.
 *
 * Built on `IconButton`, so size/style/className and the tooltip
 * infrastructure work the same as any other ui button.
 */
export default function CopyButton({
  text,
  copyLabel = "Copy",
  copiedLabel = "Copied to clipboard",
  ...iconButtonProps
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    },
    [],
  );

  const handleClick = useCallback(async () => {
    if (!navigator.clipboard) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
      resetTimerRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch {
      // Clipboard write failed (e.g. permission denied in a sandboxed iframe).
      // Silently ignore — no visible state change, consumer can layer their
      // own error UX if needed.
    }
  }, [text]);

  return (
    <IconButton
      ariaLabel={copied ? copiedLabel : copyLabel}
      tooltip={copied ? copiedLabel : copyLabel}
      onClick={handleClick}
      {...iconButtonProps}
    >
      {copied ? <IconCheck /> : <IconCopy />}
    </IconButton>
  );
}
